import { useEffect, useRef, useState } from 'react';

/**
 * Subtle scroll-driven parallax. Returns [ref, translateY (px)].
 * Disabled when:
 *  - prefers-reduced-motion is set
 *  - viewport width < 768 (mobile = no parallax for perf)
 *
 * `strength` = max travel in px (default ±40px → ~10–15% of section height).
 */
const useParallax = (strength = 40) => {
  const ref = useRef(null);
  const [y, setY] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (prefersReduced || isMobile) return;

    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // progress: -1 (just leaving above) → 1 (just entering below)
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const clamped = Math.max(-1, Math.min(1, progress));
        setY(-clamped * strength);
        raf = null;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength]);

  return [ref, y];
};

export default useParallax;
